// /app/frontend/src/components/SocialProofTicker.jsx
// Prueba Social Visual - tarjetas flotantes tipo "alguien acaba de ganar"
// estilo gaming premium (Free Fire + Candy Crush + casino mobile).
//
// Aislado: NO toca candado, spin, tickets, backend ni seguridad.
// Lee config desde GET /api/social-proof/config (público). Si falla, usa
// fallback conservador.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/social-proof-ticker.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://giroexpress-ruleta-backend.onrender.com';

/**
 * Fallback usado mientras carga el endpoint o si falla.
 * Coincide con el shape que devuelve /api/social-proof/config.
 */
export const SOCIAL_PROOF_FALLBACK = {
  activo: true,
  intervalo_segundos: 10,
  tiempo_visible: 5,
  usar_eventos_reales: false,
  mostrar_nombres: false,
  intensidad_visual: 'media',   // 'baja' | 'media' | 'alta'
  mostrar_dinero: true,
  mostrar_xp: true,
  mostrar_tickets: true,
  mostrar_giros: true,
  mostrar_especiales: true,
};

/**
 * Mock local de eventos. Cada uno tiene una `category` que se cruza con
 * los flags `mostrar_*` del config para filtrar qué se ve.
 *
 * Privacidad: aunque el config llegue con mostrar_nombres=true, este mock
 * SIEMPRE usa actores genéricos ("Un jugador", "Alguien", "Un usuario").
 * Cuando se conecte a spin_logs reales, hash o anonimización va aquí.
 */
const MOCK_EVENTS = [
  { category: 'dinero',     icon: '🎉', actor: 'Un jugador', verb: 'ganó',       prize: '$10.000 COP',       prizeTone: 'green',  time: 'hace 2 min' },
  { category: 'tickets',    icon: '🔥', actor: 'Alguien',     verb: 'desbloqueó', prize: '2 tiquetes',        prizeTone: 'yellow', time: 'hace 1 min' },
  { category: 'xp',         icon: '⭐', actor: 'Un usuario',  verb: 'ganó',       prize: '50 XP',             prizeTone: 'yellow', time: 'hace segundos' },
  { category: 'dinero',     icon: '💰', actor: 'Un jugador', verb: 'ganó',       prize: '$5.000 COP',        prizeTone: 'green',  time: 'hace 3 min' },
  { category: 'giros',      icon: '🔄', actor: 'Alguien',     verb: 'obtuvo',     prize: 'un giro adicional', prizeTone: 'yellow', time: 'hace 4 min' },
  { category: 'especiales', icon: '🏅', actor: 'Un jugador', verb: 'desbloqueó', prize: 'XP x2',             prizeTone: 'yellow', time: 'hace segundos' },
  { category: 'tickets',    icon: '🎟', actor: 'Un usuario',  verb: 'consiguió',  prize: '1 tiquete dorado',  prizeTone: 'yellow', time: 'hace 1 min' },
  { category: 'dinero',     icon: '💰', actor: 'Alguien',     verb: 'ganó',       prize: '$20.000 COP',       prizeTone: 'green',  time: 'hace 5 min' },
];

// Mezcla aleatoria simple (Fisher–Yates) para que el orden no sea repetitivo
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Mapea category → flag del config
function isCategoryEnabled(category, config) {
  if (category === 'dinero')     return config.mostrar_dinero !== false;
  if (category === 'xp')         return config.mostrar_xp !== false;
  if (category === 'tickets')    return config.mostrar_tickets !== false;
  if (category === 'giros')      return config.mostrar_giros !== false;
  if (category === 'especiales') return config.mostrar_especiales !== false;
  return true;
}

export default function SocialProofTicker() {
  const [config, setConfig] = useState(SOCIAL_PROOF_FALLBACK);
  const [current, setCurrent] = useState(null);
  const [phase, setPhase] = useState('idle');         // 'idle' | 'in' | 'out'
  const queueRef = useRef([]);
  const timersRef = useRef([]);

  // 1) Fetch config remota (con fallback silencioso)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/social-proof/config`, { method: 'GET' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        if (d && d.config && typeof d.config === 'object') {
          setConfig({ ...SOCIAL_PROOF_FALLBACK, ...d.config });
        }
      } catch (e) {
        // Falla silenciosa: ya tenemos fallback
        console.log('[SOCIAL-PROOF] usando fallback (config fetch falló)', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2) Pool filtrado por categorías habilitadas
  const pool = useMemo(() => {
    const filtered = MOCK_EVENTS.filter((e) => isCategoryEnabled(e.category, config));
    return shuffle(filtered);
  }, [config]);

  // 3) Ciclo de aparición / salida
  useEffect(() => {
    if (!config?.activo) return undefined;
    if (pool.length === 0) return undefined;   // todos los tipos filtrados

    const visibleMs  = Math.max(1, Number(config.tiempo_visible || 5)) * 1000;
    const intervalMs = Math.max(visibleMs + 1000, Number(config.intervalo_segundos || 10) * 1000);

    const popNext = () => {
      if (queueRef.current.length === 0) {
        queueRef.current = shuffle(pool);
      }
      return queueRef.current.shift();
    };

    const cycle = () => {
      setCurrent(popNext());
      setPhase('in');

      const tOut = window.setTimeout(() => setPhase('out'), visibleMs);
      timersRef.current.push(tOut);

      const tNext = window.setTimeout(() => {
        setCurrent(null);
        setPhase('idle');
        cycle();
      }, intervalMs);
      timersRef.current.push(tNext);
    };

    const tStart = window.setTimeout(cycle, 1200);
    timersRef.current.push(tStart);

    return () => {
      timersRef.current.forEach((t) => window.clearTimeout(t));
      timersRef.current = [];
    };
  }, [config, pool]);

  if (!config?.activo) return null;
  if (!current) return null;

  const intensityCls = `social-proof--${
    config.intensidad_visual === 'baja' || config.intensidad_visual === 'alta'
      ? config.intensidad_visual
      : 'media'
  }`;

  return (
    <div
      className={`spt-wrapper ${intensityCls}`}
      aria-hidden="true"
      data-testid="social-proof-ticker"
      data-intensity={config.intensidad_visual || 'media'}
    >
      <div
        className={`spt-card spt-${phase}`}
        data-testid="social-proof-card"
        data-category={current.category}
      >
        {/* Destello cruzando la tarjeta */}
        <span className="spt-shimmer" aria-hidden="true" />

        {/* Icono grande izquierda con halo */}
        <div className="spt-icon" aria-hidden="true">
          <span className="spt-icon-glyph">{current.icon}</span>
          <span className="spt-icon-halo" />
        </div>

        {/* Texto */}
        <div className="spt-text">
          <div className="spt-line-1">
            <span className="spt-actor">{current.actor}</span>
            <span className="spt-verb"> {current.verb} </span>
            <span className={`spt-prize spt-prize-${current.prizeTone}`}>{current.prize}</span>
          </div>
          <div className="spt-line-2">{current.time}</div>
        </div>

        {/* Indicador pulso "vivo" */}
        <span className="spt-pulse" aria-hidden="true" />
      </div>
    </div>
  );
}
